import { Button, Drawer, Form, Input, Modal, message, Table, Popover, Icon, Descriptions } from 'antd';
import { FormComponentProps, FormItemProps } from 'antd/lib/form';
import { ColumnProps } from 'antd/lib/table';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import 'src/components/views/Order/OrderDetailPage.less';
import { CATEGORY_ORIGINAL, EPayStatus, PAYMENT_POSTPAID, orderCanInvoice, PAYMENT_GIFT } from 'src/constants/Order';
import { IOrderInfo, IOrderRemark, ILogistics, IOrderPrescription, IOrderItem } from 'src/entity/OrderEntity';
import APIS from 'src/constants/ApiConfig';
import I18N from 'src/services/I18NService';
import OrderService from 'src/services/OrderService';
import MedicineService from 'src/services/MedicineService';
import { IPageResult } from 'src/entity/BaseResult';
import { UtilPrint, IPrintMedicineOrderData, IPrintPrescriptionOrderData, IPrintProperty } from '../../../utils/print';
import { MddCurrency, MddDecimal } from '@macrocura/utils';
import OrderInfo from './components/OrderInfo';
// import DeliveryInfo from './components/DeliveryInfo';
// import AccountInfo from './components/AccountInfo';
import MedicineSummary from './components/MedicineSummary';
import InvoiceInfo from './components/InvoiceInfo';
import LogisticsInfo, { ILogisticsEdit } from './components/LogisticsInfo';
import SendMedicines from './components/SendMedicines';
import OrderPrintService from 'src/services/OrderPrintService';
import { ILogistic } from 'src/entity/logistics';
import OrderListPage from './OrderListPage';
import PrintExpressDialog from './PrintExpressDialog';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { getMethodDes } from 'src/redux/actions/userCreator';
import { IOrderMedicineStatus, MallEdiService } from 'src/services/MallEdiService';
import InstitutionInfo from './components/InstitutionInfo';
import { EnumMedicineCategory } from 'src/constants/enum';

const { TextArea } = Input;

// 旧的剂型（草药， 颗粒， 超微颗粒）
const OLD_DOSAGE_TYPES = new Set([0, 1, 2]);
// 新的剂型（ 膏方， 散剂， 等 ）
const NEW_DOSAGE_TYPES = new Set([20, 21, 30, 31, 40, 41, 42, 50]);

interface IOrderItemTable {
  key: number;
  name: string;
  short_name: string;
  /** 药厂名称 */
  brand_name: string;
  /** 药材类型 */
  property: string;
  /** 类型 */
  type: EnumMedicineCategory;
  each_dose_amount: string;
  prescription_amount: number;
  total_dose_amount: string;
  /** 运行求和 */
  step_sum: string;
  // 西成药
  /** 厂家 */
  manufacturer: string;
  /** 药品类型 */
  west_category: string;
  /** 包装 */
  package: string;
  /** 药品单价 */
  medicine_price: number;
  /** 订单数量 */
  order_qty_str: string;
  /** 实发数量 */
  actual_qty_str: string;
  /** 订单明细项 */
  dataSource: IOrderItem;
  /** 西药剂型 */
  west_dosage_form: string;
}

/** 药品订单分组 */
interface IMedicineProcessData {
  groupKey: string;
  groupName: string;
  data: IOrderItemTable[][];
}

/** 处方订单处理数据 */
interface IPreProcessData {
  group: string;
  data: IPreProcessListData[];
  type: EnumMedicineCategory;
}

/** 处方订单列表处理数据 */
interface IPreProcessListData {
  pre: IOrderPrescription;
  items: IOrderItemTable[];
  total: number;
}

interface IInvoiceInfo {
  /** 主键 */
  id: string;
  receiver_name?: string;
  receiver_mobile?: string;
  receiver_address?: string;
  receiver_structure_address?: string;
  /** 普通发票 10 */
  invoice_type?: number;
  /** 发票状态 */
  issue_status: 'NOT_YET_ISSUED' | 'HAVE_BEEN_ISSUED';
  /** 个人抬头 10, 单位抬头  20 */
  rise_type?: number;

  personal_name?: string;

  /** 单位名称 */
  unit_name?: string;
  /** 纳税人识别号 */
  taxpayer_identification_number?: string;
  // ... 省略了其他参数

  express_number?: string;
  logistics_company?: string;
}

interface IOrderDetailPageState {
  orderLogistics: ILogistics;
  invoiceInfo: IInvoiceInfo | null;
  invoiceLogistics: ILogistics | null;

  /** 药品物流的结构化地址 */
  structureAddress: string;
  /** 发票物流的结构化地址 */
  invoiceStructureAddress: string;

  /** 电子处方抽屉 */
  electrPrecsVisible: boolean;
  /** 打开抽屉后自动打印 */
  autoPrint: boolean;
  /** 打开抽屉后， 显示运单详情 */
  logisticSheet: boolean;
  /** 发票开具状态 */
  invoiceIssuedStatus?: boolean;
  /** 是否可编辑发票开具状态，待处理和已处理可以编辑 */
  invoiceIssuedStatusDisable: boolean;
  /**
   * 订单是否参与分账:
   * 参与--新流程(通联)(不含记账支付):
   *   应收金额取 user_receivable_profit_amount字段
   *   实收金额取 user_received_profit_amount字段
   * 不参--记账支付与旧流程:
   *   应收金额取 transfer_amount_after_tax字段
   *   不显示分账信息
   * 注:
   * 1, 根据platform_name是否有值判断 新旧流程
   * 2, 若新流程中 user_receivable_profit_amount,
   * user_received_profit_amount字段为null，
   * 代表此合作商不参与分账，不显示分账信息;
   */
  isSubAccount: boolean;

  /** 打印运单对话框 */
  expressVisible: boolean;

  /** 是否开通药品订单接口 */
  sendMedicineConfig: boolean;
  /** 发送药品订单状态 */
  sendMedicineOrderStatus: boolean;
}

interface IOrderDetailFormProps extends FormComponentProps, RouteComponentProps {
  orderInfo: IOrderInfo;
  isAgent: boolean;
  userInfo: IAgentInfo | IDrugStoreInfo | undefined;
  clearOrder: () => void;
}

class OrderDetailForm extends React.Component<IOrderDetailFormProps, IOrderDetailPageState> {
  canSubmit: boolean = true;

  private iframe: HTMLIFrameElement | null = null;
  private iframeLoaded = false;
  private logisticData: ILogistic;

  constructor(props: IOrderDetailFormProps) {
    super(props);
    const { orderInfo } = props;
    const { platform_name, payment_type, user_profit_amount } = orderInfo;
    const userReceivedProfitAmount = user_profit_amount ? user_profit_amount.user_received_profit_amount : undefined;
    const userReceivableProfitAmount = user_profit_amount ? user_profit_amount.user_receivable_profit_amount : undefined;
    let isSubAccount = false;
    do {
      if (payment_type === PAYMENT_POSTPAID || payment_type === PAYMENT_GIFT || !platform_name) {
        // 老流程 or 记账模式
        break;
      }
      if (typeof userReceivedProfitAmount !== 'number' && typeof userReceivableProfitAmount !== 'number') {
        // 不参与分账
        break;
      }
      isSubAccount = true;
    } while (false);

    const express = orderInfo.express;

    this.state = {
      orderLogistics: {
        logisticsCompany: express?.express_company || '',
        expressNumber: express?.express_no || '',
      },

      invoiceInfo: null,
      invoiceLogistics: null,
      structureAddress: '',
      invoiceStructureAddress: '',
      electrPrecsVisible: false,
      autoPrint: false,
      logisticSheet: false,
      invoiceIssuedStatusDisable: this.setInvoiceIssuedStatusDisable(this.props.orderInfo.pay_status),
      isSubAccount,
      expressVisible: false,
      sendMedicineConfig: false,
      sendMedicineOrderStatus: false,
    };
    this.getStructureAddress();
    this.getInvoiceInfo();
    this.getSendOrderMedicineConfig();
  }

  async componentDidMount() {
    await OrderPrintService.fetchTakeMethods();
  }
  componentWillUnmount() {
    this.props.clearOrder();
    this.setState = () => {
      return;
    };
  }

  /**
   * @description 设置发票是否开具的编辑状态值，true不可以编辑（取消状态），false:可编辑
   */
  setInvoiceIssuedStatusDisable = (payStatus: number): boolean => {
    return orderCanInvoice(payStatus);
  };

  /**
   * 暂存
   *
   * @memberof OrderDetailForm
   */
  handleHold = async (e: React.SyntheticEvent) => {
    try {
      const { orderInfo } = this.props;
      const remarkObj: IOrderRemark = {
        agent_remarks: this.props.form.getFieldValue('agent_remarks'),
      };
      await OrderService.updateOrderAgentRemarks(orderInfo.id, remarkObj);
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_save_failure'));
      }
    }
  };

  /**
   * 处理完毕
   *
   * @memberof OrderDetailForm
   */
  handleFinish = async () => {
    try {
      const { orderInfo } = this.props;
      const newRemarks = this.props.form.getFieldValue('agent_remarks');
      if (orderInfo.agent_remarks !== newRemarks) {
        await OrderService.updateOrderAgentRemarks(orderInfo.id, { agent_remarks: newRemarks });
      }

      await OrderService.finishOrder(orderInfo.out_trade_no);
      OrderListPage.clearCache();
      this.goback();
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_save_failure'));
      }
    }
  };

  handleUpdateLogistics = async (value: ILogisticsEdit) => {
    try {
      const data = {
        logistics_company: value.logisticsCompany || '',
        express_number: value.expressNumber || '',
      };
      if (value.logisticsType === 1 && !!this.state.invoiceInfo) {
        const res = await OrderService.updateInvoiceLogistics(this.state.invoiceInfo.id, data);
        if (!!res) {
          this.setState({
            invoiceLogistics: {
              logisticsCompany: data.logistics_company,
              expressNumber: data.express_number,
            },
          });
        }
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_save_failure'));
      }
    }
  };

  handleCloseDrawer() {
    this.setState({ electrPrecsVisible: false, autoPrint: false });
  }

  // 点击显示电子处方
  handleShowDrawer(auto: boolean) {
    this.setState({ electrPrecsVisible: true, autoPrint: auto, logisticSheet: false });
    if (this.iframeLoaded) {
      this.pushElectrPresc();
    }
  }

  /**
   * @description 更多物流信息
   */
  handleLogisticInfo(logisticData: ILogistic) {
    this.setState({ electrPrecsVisible: true, logisticSheet: true });
    this.logisticData = logisticData;
    if (this.iframeLoaded) {
      this.pushLogistic();
    }
  }

  printOrderList = async () => {
    const { orderInfo } = this.props;
    await OrderPrintService.printOrderList(orderInfo);
    // 更新打印时间
    OrderService.updatePrintDate(orderInfo.id);
  };

  printOrderMedicineList = () => {
    const { orderInfo } = this.props;
    const dataList: IPrintMedicineOrderData[] = [];
    for (const item of orderInfo.items || []) {
      // const unitKey = MedicineService.getEnumUnitKey(item.retail_unit);
      const unitKey = item.retail_unit;
      dataList.push({
        short_name: item.short_name + `(${item.equivalent}${I18N.get('common_unit_gram')})`,
        med_type: MedicineService.getMedicineType(item.dosage_form),
        brand_name: item.brand_name,
        // order_volume_dose: `${item.quantity}${I18N.get(unitKey)}`,
        order_volume_dose: `${item.quantity}${unitKey}`,
      });
    }
    const properties: IPrintProperty[] = [
      {
        field: 'short_name',
        displayName: `${I18N.get('med_agent')}(${I18N.get('med_equivalent')})`,
      },
      {
        field: 'med_type',
        displayName: `${I18N.get('med_type')}`,
      },
      {
        field: 'brand_name',
        displayName: `${I18N.get('pharmacy_brand')}`,
      },
      {
        field: 'order_volume_dose',
        displayName: `${I18N.get('order_volume_dose')}`,
      },
    ];
    UtilPrint.printJsonData(I18N.get('common_drug_list'), dataList, properties);
  };

  printOrderPrescriptionList = () => {
    const { orderInfo } = this.props;

    const groupDataList: IPrintPrescriptionOrderData[][] = [];
    for (const item of orderInfo.items || []) {
      const index = item.group || 0;
      groupDataList[index] = [];
    }

    for (const item of orderInfo.items || []) {
      // const unitKey = MedicineService.getEnumUnitKey(item.retail_unit);
      const unitKey = item.retail_unit;

      let prescriptionAmount: number = 1;
      if (item.group !== undefined) {
        const tempObj = orderInfo.prescriptions.find((t) => t.group === item.group);
        if (!!tempObj) {
          prescriptionAmount = tempObj.amount;
        }
      }
      const eachDose = Number(item.quantity / prescriptionAmount).toFixed(2);

      groupDataList[item.group || 0].push({
        short_name: item.short_name + `(${item.equivalent}${I18N.get('common_unit_gram')})`,
        med_type: MedicineService.getMedicineType(item.dosage_form),
        brand_name: item.brand_name,
        // each_dose_amount: `${eachDose}${I18N.get(unitKey)}`,
        each_dose_amount: `${eachDose}${unitKey}`,
        prescription_amount: `${prescriptionAmount}`,
        // total_dose_amount: `${item.quantity}${I18N.get(unitKey)}`,
        total_dose_amount: `${item.quantity}${unitKey}`,
        order_category_prescription: `${I18N.get('order_category_prescription')}${(item.group || 0) + 1}`,
      });

      // dataList.push({
      //   short_name: item.short_name + `(${item.equivalent}${I18N.get('common_unit_gram')})`,
      //   med_type: MedicineService.getMedicineType(item.dosage_form),
      //   brand_name: item.brand_name,
      //   each_dose_amount: `${eachDose}${I18N.get(unitKey)}`,
      //   prescription_amount: prescriptionAmount,
      //   total_dose_amount: `${item.quantity}${I18N.get(unitKey)}`,
      // });
    }
    const properties: IPrintProperty[] = [
      {
        field: 'short_name',
        displayName: `${I18N.get('med_agent')}(${I18N.get('med_equivalent')})`,
      },
      {
        field: 'med_type',
        displayName: `${I18N.get('med_type')}`,
      },
      {
        field: 'brand_name',
        displayName: `${I18N.get('pharmacy_brand')}`,
      },
      {
        field: 'each_dose_amount',
        displayName: `${I18N.get('order_each_dose')}`,
      },
      {
        field: 'prescription_amount',
        displayName: `${I18N.get('order_prescription_amount')}`,
      },
      {
        field: 'total_dose_amount',
        displayName: `${I18N.get('order_volume_dose')}`,
      },
    ];
    if (groupDataList.length > 1) {
      properties.push({
        field: 'order_category_prescription',
        displayName: `${I18N.get('order_category_prescription')}`,
      });
    }

    let dataList: IPrintPrescriptionOrderData[] = [];
    for (const data of groupDataList) {
      dataList = dataList.concat(data);
    }
    UtilPrint.printJsonData(I18N.get('common_drug_list'), dataList, properties);
  };

  handleSendGood = () => {
    const { orderInfo } = this.props;
    const title = orderInfo.express ? `运单号${orderInfo.express.express_no}, 是否确认发货?` : '订单尚未关联运单号，是否确认发货？';
    const self = this;
    Modal.confirm({
      title,
      content: '发货后将不能更改运单',
      okText: '确定',
      cancelText: '取消',
      onOk() {
        self.handleFinish();
      },
      onCancel() { },
    });
  };

  /** 自提发货 */
  handleSelfSendGood = async () => {
    const { orderInfo } = this.props;
    const self = this;
    Modal.confirm({
      title: '是否确认发货',
      content: '',
      okText: '确定',
      cancelText: '取消',
      onOk() {
        OrderService.finishOrder(orderInfo.out_trade_no).then((success) => {
          if (!!success) {
            // 刷新列表
            OrderListPage.clearCache();
            self.goback();
          }
        });
      },
      onCancel() { },
    });
  };

  /** 获取药品发货清单 */
  getMedicineDeliverList = async (order_id: string) => {
    const url = `${APIS.domainUrl(APIS['export-deliver-list'])}`;
    OrderService.exportData(url, '发货清单', { order_id });
  }

  goback = () => {
    this.props.history.goBack();
  };

  renderContent = (value: any, row: any, index: number) => {
    return {
      children: value,
      props: {},
    };
  };

  /**
   * 发送药品清单回调函数
   */
  sendMedicineCallback = async (statusList: IOrderMedicineStatus[]) => {
    const successList = (statusList || []).filter((i) => i.status === 'SUCCESS');
    // 如果全部success的且长度不为0则 可以发货
    if (successList.length === statusList.length && statusList.length) {
      this.setState({
        sendMedicineOrderStatus: true,
      });
    }
  };

  /**
   * 药品订单中药列
   *
   * @readonly
   * @type {Array<ColumnProps<IOrderItemTable>>}
   * @memberof OrderDetailForm
   */
  get originalColumns(): Array<ColumnProps<IOrderItemTable>> {
    return [
      {
        title: `${I18N.get('med_agent')}(${I18N.get('med_equivalent')})`,
        dataIndex: 'short_name',
        key: 'short_name',
        render: (text: any, record: IOrderItemTable): React.ReactNode => {
          return (
            <span>
              {(!!record.dataSource.is_all_refund || !!record.dataSource.is_partial_refund) && (
                <span style={{ color: '#FF4D4F' }}>{!!record.dataSource.is_all_refund ? '[已退款]' : '[部分退款]'} </span>
              )}
              {text}
            </span>
          );
        },
      },
      {
        title: `${I18N.get('med_type')}`,
        dataIndex: 'property',
        key: 'property',
      },
      {
        title: `${I18N.get('pharmacy_brand')}`,
        dataIndex: 'brand_name',
        key: 'brand_name',
      },
      {
        title: `订购数量`,
        dataIndex: 'order_qty_str',
        key: 'order_qty_str',
      },
      {
        title: `实发数量`,
        dataIndex: 'actual_qty_str',
        key: 'actual_qty_str',
        render: (text: any, record: IOrderItemTable): React.ReactNode => {
          return !!record.dataSource.is_all_refund || !!record.dataSource.is_partial_refund ? (
            <span style={{ color: '#FF4D4F' }}>{text}</span>
          ) : (
            <span>{text}</span>
          );
        },
      },
      {
        title: `${I18N.get('order_drugs_amount')}`,
        dataIndex: 'total_dose_amount',
        key: 'total_dose_amount',
      },
    ];
  }

  /**
   * 药品订单中成药列
   *
   * @readonly
   * @type {Array<ColumnProps<IOrderItemTable>>}
   * @memberof OrderDetailForm
   */

  get originalCpmColumns(): Array<ColumnProps<IOrderItemTable>> {
    return [
      {
        title: `药品名称`,
        dataIndex: 'name',
        key: 'name',
        render: (text: any, record: IOrderItemTable): React.ReactNode => {
          return (
            <span>
              {(!!record.dataSource.is_all_refund || !!record.dataSource.is_partial_refund) && (
                <span style={{ color: '#FF4D4F' }}>{!!record.dataSource.is_all_refund ? '[已退款]' : '[部分退款]'} </span>
              )}
              {text}
            </span>
          );
        },
      },
      {
        title: `剂型`,
        dataIndex: 'west_dosage_form',
        key: 'west_dosage_form',
      },
      {
        title: `包装`,
        dataIndex: 'package',
        key: 'package',
        render: (text: any, record: IOrderItemTable): React.ReactNode => {
          return <span>{record.dataSource.package || ''}</span>;
        },
      },
      {
        title: `厂家（或机构）`,
        dataIndex: 'manufacturer',
        key: 'manufacturer',
      },
      {
        title: `订购数量`,
        dataIndex: 'order_qty_str',
        key: 'order_qty_str',
      },
      {
        title: `实发数量`,
        dataIndex: 'actual_qty_str',
        key: 'actual_qty_str',
        render: (text: any, record: IOrderItemTable): React.ReactNode => {
          return !!record.dataSource.is_all_refund || !!record.dataSource.is_partial_refund ? (
            <span style={{ color: '#FF4D4F' }}>{text}</span>
          ) : (
            <span>{text}</span>
          );
        },
      },
      {
        title: `${I18N.get('order_drugs_amount')}`,
        dataIndex: 'total_dose_amount',
        key: 'total_dose_amount',
      },
    ];
  }
  /**
   * 处方西成药列表
   */
  get preCpmColumns(): Array<ColumnProps<IOrderItemTable>> {
    return [
      {
        title: `药品全称`,
        dataIndex: 'short_name',
        key: 'short_name',
      },
      {
        title: `分类`,
        dataIndex: 'west_category',
        key: 'west_category',
      },
      {
        title: `包装`,
        dataIndex: 'package',
        key: 'package',
      },
      {
        title: `厂家`,
        dataIndex: 'manufacturer',
        key: 'manufacturer',
      },
      {
        title: `单价`,
        dataIndex: 'medicine_price',
        key: 'medicine_price',
      },
      {
        title: `${I18N.get('order_volume_dose')}/${I18N.get('order_drugs_amount')}`,
        dataIndex: 'total_dose_amount',
        key: 'total_dose_amount',
      },
    ];
  }

  /**
   * 处方订单列
   *
   * @readonly
   * @type {Array<ColumnProps<IOrderItemTable>>}
   * @memberof OrderDetailForm
   */
  get prescriptionColumns(): Array<ColumnProps<IOrderItemTable>> {
    return [
      {
        title: `${I18N.get('med_agent')}(${I18N.get('med_equivalent')})`,
        dataIndex: 'short_name',
        key: 'short_name',
      },
      {
        title: `${I18N.get('med_type')}`,
        dataIndex: 'property',
        key: 'property',
      },
      {
        title: `${I18N.get('pharmacy_brand')}`,
        dataIndex: 'brand_name',
        key: 'brand_name',
      },
      {
        title: `${I18N.get('order_each_dose')}`,
        dataIndex: 'each_dose_amount',
        key: 'each_dose_amount',
      },
      {
        title: `${I18N.get('order_prescription_amount')}`,
        dataIndex: 'prescription_amount',
        key: 'prescription_amount',
      },
      {
        title: `${I18N.get('order_volume_dose')} / ${I18N.get('order_drugs_amount')}`,
        dataIndex: 'total_dose_amount',
        key: 'total_dose_amount',
      },
      // {
      //   title: '运行求和',
      //   dataIndex: 'step_sum',
      //   key: 'step_sum',
      // },
    ];
  }

  onChangeIssuedStatus = async (status: boolean) => {
    if (!!this.state.invoiceInfo && !!this.state.invoiceInfo.id) {
      const operation = status ? 'ISSUE' : 'CANCEL_ISSUE';
      const res = await OrderService.changeInvoiceStatus(this.state.invoiceInfo.id, operation);
      if (!!res) {
        await this.getInvoiceInfo();
        message.success(I18N.get(status ? 'order_invoice_change_to_issued' : 'order_invoice_change_to_not_issued', false));
      } else {
        message.error(I18N.get('err_invoice_status_change_failure', false));
      }
    }
  };

  /**
   * @description
   * @author Ze Hui
   * @date 09/10/2020
   * @param {*} group： 处方分组信息
   * @param {*} item: 草药
   */
  getPreAmount(group: IOrderPrescription, item: IOrderItem) {
    // if (OLD_DOSAGE_TYPES && OLD_DOSAGE_TYPES.has(item.dosage_form)) {
    //   return Number(item.quantity / group.amount).toFixed(2);
    // }

    // if (OLD_DOSAGE_TYPES && NEW_DOSAGE_TYPES.has(item.dosage_form)) {
    const { orderInfo } = this.props;
    const { e_prescription_snapshot } = orderInfo;

    if (e_prescription_snapshot) {
      // 从快照中获取每剂量信息
      const { solve_prescriptions } = e_prescription_snapshot;
      const targetPrescription = solve_prescriptions.find((ele) => ele.id === group.solve_prescription_id);
      if (targetPrescription) {
        const meds = targetPrescription.presc_items;
        const targetMed = meds.find((med) => med.sku === item.medicine_sku);
        if (targetMed) {
          return targetMed.pharmacy_quantity.toString();
        }
      }
    }
    // }

    return '';
  }

  /**
   * 获取草药剂量服用次数描述
   */
  getDecoctionUseTimesDesc(preInfo: IOrderPrescription): string {
    let decoctionAmountDesc = '';
    const useTimes = preInfo.use_times;
    const amount = preInfo.amount;
    decoctionAmountDesc = `${!!useTimes && !!amount
      ? I18N.get('common_total_prefix') +
      amount +
      I18N.get('order_prescription_amount_unit') +
      '， ' +
      I18N.get('order_decoction_use_times_prefix') +
      useTimes +
      I18N.get('order_decoction_use_times_stuffix') +
      '。 '
      : ' '
      }`;
    return decoctionAmountDesc;
  }

  /** 获取处方加工方式 */
  getProcessingMethod(preInfo: IOrderPrescription): string {
    const method = preInfo.processing_method;
    if (!!method) {
      return getMethodDes(method);
    }
    return '';
  }

  /** 中药/西药处方转换成界面渲染数据结构 */
  processingMedItem(
    pre: IOrderPrescription,
    data: IOrderItem[],
    currencySymbol: string
  ): {
    data: IOrderItemTable[];
    total: number;
  } {
    const render: IOrderItemTable[] = [];
    let stepSum = 0;
    const g = I18N.get('common_unit_gram');
    let total = 0;
    for (const item of data) {
      const amount = pre.amount || 1;
      let eachDose = Number(item.quantity / amount).toFixed(2);
      if (NEW_DOSAGE_TYPES && NEW_DOSAGE_TYPES.has(item.dosage_form)) {
        eachDose = this.getPreAmount(pre, item);
      }
      stepSum = MddDecimal.decimalSumCeil([stepSum], item.quantity);
      const totalAmount = MddDecimal.decimalCeil(item.final_price).toFixed(2);
      if (!total) {
        total = Number(totalAmount);
      } else {
        total = MddDecimal.decimalSumCeil([total, Number(totalAmount)]);
      }
      const TakeMethods = OrderPrintService.takeMethods;
      // const unitKey = MedicineService.getEnumUnitKey(item.retail_unit);
      const unitKey = item.retail_unit;
      const medicine: IOrderItemTable = {
        key: item.id,
        name: item.name,
        short_name: item.short_name + `(${item.take_method_id ? TakeMethods[item.take_method_id] + '  ' : ''}${item.equivalent}${g})`,
        brand_name: item.brand_name,
        property: MedicineService.getMedicineType(item.dosage_form),
        // each_dose_amount: `${eachDose}${I18N.get(unitKey)}`,
        each_dose_amount: `${eachDose}${unitKey}`,
        prescription_amount: amount,
        total_dose_amount: '',
        // step_sum: `${stepSum}${I18N.get(unitKey)}`,
        step_sum: `${stepSum}${unitKey}`,
        type: item.type,
        manufacturer: item.manufacturer || '',
        west_category: '',
        package: '',
        medicine_price: 0,
        order_qty_str: '',
        actual_qty_str: '',
        dataSource: item,
        west_dosage_form: item.west_dosage_form,
      };
      if (pre.type === EnumMedicineCategory.中成药) {
        medicine.short_name = item.name;
        medicine.package = item.package;
        medicine.west_category = item.west_category;
        medicine.medicine_price = item.medicine_price;
        medicine.total_dose_amount = `${item.quantity}${item.retail_unit} / ${currencySymbol}${totalAmount}`;
      } else {
        // medicine.total_dose_amount = `${item.quantity}${unitKey ? I18N.get(unitKey) : ''} / ${currencySymbol}${totalAmount}`;
        medicine.total_dose_amount = `${item.quantity}${unitKey} / ${currencySymbol}${totalAmount}`;
      }
      render.push(medicine);
    }
    return {
      data: render,
      total,
    };
  }

  render() {
    const { userInfo, orderInfo, isAgent } = this.props;
    const drugStoreInfo = userInfo as IDrugStoreInfo;
    const readonlyTextBox = this.props.isAgent || orderInfo.pay_status >= EPayStatus.已发货;
    const { getFieldDecorator } = this.props.form;

    const formItemLayout12: FormItemProps = {
      labelCol: { span: 6 },
      wrapperCol: { span: 18 },
    };

    const g = I18N.get('common_unit_gram');
    // 是否是处方订单 和 门诊订单
    const isP = orderInfo.category !== CATEGORY_ORIGINAL;

    const currencySymbol = MddCurrency.getCurrencySymbol(orderInfo.currency);
    const isCpmMed = orderInfo.items[0].type === EnumMedicineCategory.中成药;
    const columns: Array<ColumnProps<IOrderItemTable>> =
      orderInfo.category === CATEGORY_ORIGINAL ? (isCpmMed ? this.originalCpmColumns : this.originalColumns) : this.prescriptionColumns;
    const cpmColumns: Array<ColumnProps<IOrderItemTable>> = this.preCpmColumns;
    /** 是中药药品订单 */
    const isOriginalTcm = orderInfo.category === CATEGORY_ORIGINAL && !isCpmMed;
    // const prescriptionAmount = orderInfo.prescription_amount > 0 ? orderInfo.prescription_amount : 14;
    // 药品金额分组求和
    const groupAmount: number[] = [];
    // 药品数量分布求和
    const stepSum: number[] = [];

    // 表格数据
    // const orderItemTableData: IOrderItemTable[][] = [];
    const medicineTableData: IMedicineProcessData[] = [];
    const medicineMap = new Map<string, IOrderItem[]>();
    const preTableData: IPreProcessData[] = [];
    if (!isP) {
      // 药品订单
      for (const item of orderInfo.items || []) {
        if (medicineMap.has(item.medicine_group_uuid)) {
          const tempItems = medicineMap.get(item.medicine_group_uuid) || [];
          tempItems.push(item);
        } else {
          medicineMap.set(item.medicine_group_uuid, [item]);
        }
      }

      medicineMap.forEach((value: IOrderItem[], key: string) => {
        let groupName = '';
        const orderItemTableData: IOrderItemTable[][] = [];
        for (const item of value || []) {
          groupName = item.medicine_group_name;
          let prescriptionAmount: number = 1;
          let tempObj: IOrderPrescription | undefined;
          if (item.group !== undefined) {
            tempObj = orderInfo.prescriptions.find((t) => t.group === item.group);
            if (!!tempObj) {
              prescriptionAmount = tempObj.amount;
            }
          }

          let eachDose = Number(item.quantity / prescriptionAmount).toFixed(2);

          // 新剂型要按照新的方式计算每剂量
          if (tempObj && NEW_DOSAGE_TYPES && NEW_DOSAGE_TYPES.has(item.dosage_form)) {
            eachDose = this.getPreAmount(tempObj, item);
          }

          const totalAmount = MddDecimal.decimalCeil(item.final_price).toFixed(2);

          // 根据处方订单/药品订单判断单位;处方：零售单位,药品：批发单位;
          let unitKey;
          if (!isP) {
            // unitKey = MedicineService.getEnumUnitKey(item.wholesale_unit || '');
            unitKey = item.wholesale_unit;
          }

          const group = item.group || 0;
          if (orderItemTableData[group] == null) {
            orderItemTableData[group] = [];
          }

          // 计算每步的求和
          if (stepSum[group] == null) {
            stepSum[group] = item.quantity;
          } else {
            stepSum[group] = MddDecimal.decimalSumCeil([stepSum[group], item.quantity]);
          }
          const TakeMethods = OrderPrintService.takeMethods;
          orderItemTableData[group].push({
            key: item.id,
            name: item.name,
            short_name:
              item.short_name +
              `(${item.take_method_id ? TakeMethods[item.take_method_id] + '  ' : ''}${isOriginalTcm ? item.wholesale_equivalent : item.equivalent}${g})`,
            brand_name: item.brand_name,
            property: MedicineService.getMedicineType(item.dosage_form),
            // each_dose_amount: `${eachDose}${I18N.get(unitKey)}`,
            each_dose_amount: `${eachDose}${unitKey}`,
            prescription_amount: prescriptionAmount,
            total_dose_amount: `${currencySymbol}${totalAmount}`,
            // step_sum: `${stepSum[group]}${I18N.get(unitKey)}`,
            step_sum: `${stepSum[group]}${unitKey}`,
            type: item.type,
            manufacturer: item.manufacturer,
            west_category: '',
            package: item.package || '',
            medicine_price: 0,
            // order_qty_str: `${item.quantity}${unitKey ? I18N.get(unitKey) : ''}`,
            order_qty_str: `${item.quantity}${unitKey}`,
            // actual_qty_str: `${item.practical_quantity}${unitKey ? I18N.get(unitKey) : ''}`,
            actual_qty_str: `${item.practical_quantity}${unitKey}`,
            dataSource: item,
            west_dosage_form: item.west_dosage_form,
          });

          // 计算每组的总计金额
          if (groupAmount[group] == null) {
            groupAmount[group] = Number(totalAmount);
          } else {
            groupAmount[group] = MddDecimal.decimalSumCeil([groupAmount[group], Number(totalAmount)]);
          }
        }
        medicineTableData.push({
          groupKey: key,
          groupName,
          data: orderItemTableData,
        });
      });

      // for (const item of orderInfo.items || []) {
      //   let prescriptionAmount: number = 1;
      //   let tempObj: IOrderPrescription | undefined;
      //   if (item.group !== undefined) {
      //     tempObj = orderInfo.prescriptions.find((t) => t.group === item.group);
      //     if (!!tempObj) {
      //       prescriptionAmount = tempObj.amount;
      //     }
      //   }

      //   let eachDose = Number(item.quantity / prescriptionAmount).toFixed(2);

      //   // 新剂型要按照新的方式计算每剂量
      //   if (tempObj && NEW_DOSAGE_TYPES && NEW_DOSAGE_TYPES.has(item.dosage_form)) {
      //     eachDose = this.getPreAmount(tempObj, item); // Number(item.quantity / prescriptionAmount).toFixed(2);
      //   }

      //   // const eachAmount = MathFunc.decimalCeil(item.final_price / prescriptionAmount).toFixed(2);
      //   const totalAmount = MddDecimal.decimalCeil(item.final_price).toFixed(2);
      //   // const eachDose = (item.quantity / prescriptionAmount).toFixed(2);
      //   // const eachAmount = ((item.quantity * item.medicine_price) / prescriptionAmount).toFixed(2);
      //   // const totalAmount = (item.quantity * item.medicine_price).toFixed(2);

      //   // 根据处方订单/药品订单判断单位;处方：零售单位,药品：批发单位;
      //   let unitKey;
      //   // if (isP) {
      //   //   unitKey = MedicineService.getEnumUnitKey(item.retail_unit);
      //   // }
      //   if (!isP) {
      //     unitKey = MedicineService.getEnumUnitKey(item.wholesale_unit || '');
      //   }

      //   const group = item.group || 0;
      //   if (orderItemTableData[group] == null) {
      //     orderItemTableData[group] = [];
      //   }

      //   // 计算每步的求和
      //   if (stepSum[group] == null) {
      //     stepSum[group] = item.quantity;
      //   } else {
      //     stepSum[group] = MddDecimal.decimalSumCeil([stepSum[group], item.quantity]);
      //   }

      //   orderItemTableData[group].push({
      //     key: item.id,
      //     short_name: item.short_name + `(${item.take_method_id ? TakeMethods[item.take_method_id] + '  ' : ''}${item.equivalent}${g})`,
      //     brand_name: item.brand_name,
      //     property: MedicineService.getMedicineType(item.dosage_form),
      //     each_dose_amount: `${eachDose}${I18N.get(unitKey)}`,
      //     prescription_amount: prescriptionAmount,
      //     total_dose_amount: `${item.quantity}${unitKey ? I18N.get(unitKey) : ''} / ${currencySymbol}${totalAmount}`,
      //     step_sum: `${stepSum[group]}${I18N.get(unitKey)}`,
      //     type: item.type,
      //     manufacturer: '',
      //     west_category: '',
      //     package: '',
      //     medicine_price: 0,
      //   });

      //   // 计算每组的总计金额
      //   if (groupAmount[group] == null) {
      //     groupAmount[group] = Number(totalAmount);
      //   } else {
      //     // groupAmount[group] += Number(totalAmount);
      //     groupAmount[group] = MddDecimal.decimalSumCeil([groupAmount[group], Number(totalAmount)]);
      //   }
      // }
    } else {
      // for (const item of orderInfo.items || []) {
      // }
      // 处方订单
      const items = orderInfo.items || [];
      const preList: Map<string, IPreProcessData> = new Map();
      for (const pre of orderInfo.prescriptions || []) {
        const { medicine_group_uuid, group } = pre;
        const findMed = items.filter((item) => item.group === group);
        const { data, total } = this.processingMedItem(pre, findMed, currencySymbol);
        const newItem: IPreProcessListData = {
          pre,
          items: data,
          total,
        };
        const newValue: IPreProcessData = {
          type: pre.type,
          group: pre.medicine_group_name,
          data: [newItem],
        };
        if (preList.has(medicine_group_uuid)) {
          const value = preList.get(medicine_group_uuid);
          if (value) {
            newValue.data = (value.data || []).concat(newItem);
            preList.set(pre.medicine_group_uuid, newValue);
          } else {
            preList.set(pre.medicine_group_uuid, newValue);
          }
        } else {
          preList.set(pre.medicine_group_uuid, newValue);
        }
      }
      for (const pre of preList) {
        preTableData.push(pre[1]);
      }
    }

    const groupNames: string[] = [];
    if (orderInfo.category === CATEGORY_ORIGINAL) {
      groupNames.push(I18N.get('common_drug_list'));
    }

    const isShowOtherRequest = orderInfo.is_provide_prescription === false;

    // 订单流程(新|老)
    const { isSubAccount } = this.state;
    // 应收金额
    let receivableAmount = orderInfo.transfer_amount_after_tax;
    const { user_profit_amount: profitAmount } = orderInfo;
    if (isSubAccount && profitAmount) {
      // 参与分账则取user_receivable_profit_amount字段(后台单位分)
      if (typeof profitAmount.user_receivable_profit_amount === 'number') {
        receivableAmount = MddDecimal.decimalCeil(profitAmount.user_receivable_profit_amount / 100).toFixed(2);
      } else {
        receivableAmount = (0).toFixed(2);
      }
    }
    // 实收金额(后台单位分)
    let receivedAmount = (0).toFixed(2);
    if (profitAmount && typeof profitAmount.user_received_profit_amount === 'number') {
      receivedAmount = (profitAmount.user_received_profit_amount / 100).toFixed(2);
    }

    const drawUrl = this.state.logisticSheet
      ? APIS.webUrl('/ep/index.html#/logistic/sheet')
      : this.state.autoPrint
        ? APIS.webUrl('/ep/index.html#/ele/print')
        : APIS.webUrl('/ep/index.html#/ele/record');
    const { sendMedicineConfig, sendMedicineOrderStatus } = this.state;
    return (
      <div className="order-detail">
        <div style={{ marginBottom: '24px', textAlign: 'right' }}>
          <Button type="primary" onClick={this.goback}>
            {I18N.get('common_back')}
          </Button>
        </div>

        <Form>
          {/* 基本信息 */}
          <OrderInfo orderInfo={orderInfo} isSubAccount={isSubAccount} />

          {/* 医疗机构 */}
          {!isP && <InstitutionInfo orderInfo={orderInfo} />}

          {/* 邮寄信息 */}
          {/* <DeliveryInfo orderInfo={orderInfo} structureAddress={this.state.structureAddress} /> */}

          {/* 物流信息 + 邮寄信息 */}
          <LogisticsInfo
            orderInfo={orderInfo}
            isAgent={isAgent}
            invoiceLogistics={this.state.invoiceLogistics}
            orderLogistics={this.state.orderLogistics}
            changeLogistics={this.handleUpdateLogistics}
            handleLogisticInfo={(value) => this.handleLogisticInfo(value)}
            structureAddress={this.state.structureAddress}
          />

          {/* 发票信息 */}
          {!!this.state.invoiceInfo && this.state.invoiceLogistics && (
            <InvoiceInfo
              orderInfo={orderInfo}
              invoiceInfo={this.state.invoiceInfo}
              address={this.state.invoiceStructureAddress}
              isAgent={isAgent}
              issueStatue={!!this.state.invoiceIssuedStatus}
              disable={this.state.invoiceIssuedStatusDisable}
              onChange={this.onChangeIssuedStatus}
            />
          )}

          {/* 处方订单或药品订单 中药明细 */}
          {!isP &&
            medicineTableData.map((groupObj) => (
              <div key={groupObj.groupKey}>
                <div className="div-row">
                  <div
                    className="div-col-3 text-align-right"
                    style={{ color: '#1FB8CD', marginLeft: '16px', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}
                  >
                    {groupObj.groupName}：
                  </div>
                </div>
                <div>
                  {groupObj.data.map((tableData, index) => {
                    return (
                      <div className="div-row" key={index}>
                        <div className="div-col-3 text-align-right">{groupNames[index]}</div>
                        <div className="div-col-15 order_info_table">
                          <div className="div-row">
                            <div className="div-col-24">
                              <Table dataSource={tableData} columns={columns} pagination={false} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              // orderItemTableData.map((tableData, index) => (
              //   <div className="div-row" key={index}>
              //     <div className="div-col-3 text-align-right">{groupNames[index]}</div>
              //     <div className="div-col-15 order_info_table">
              //       <div className="div-row">
              //         <div className="div-col-24">
              //           <Table dataSource={tableData} columns={columns} pagination={false} />
              //         </div>
              //       </div>
              //     </div>
              //   </div>
            ))}
          {isP &&
            preTableData.map((dataList, listIndex) => (
              <div key={listIndex}>
                <div className="div-row">
                  <div
                    className="div-col-3 text-align-right"
                    style={{ color: '#1FB8CD', marginLeft: '16px', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}
                  >
                    {dataList.group}：
                  </div>
                </div>
                <div>
                  {dataList.data.map((tableData, index) => {
                    return (
                      <div className="div-row" key={index}>
                        <div className="div-col-3 text-align-right">
                          {dataList.type === EnumMedicineCategory.中药
                            ? `中药处方${dataList.data.length === 1 ? '' : index + 1}`
                            : `成药处方${dataList.data.length === 1 ? '' : index + 1}`}
                        </div>
                        <div className="div-col-15 order_info_table">
                          <div className="div-row">
                            <div className="div-col-24">
                              <Table
                                dataSource={tableData.items}
                                columns={dataList.type === EnumMedicineCategory.中药 ? columns : cpmColumns}
                                pagination={false}
                              />
                            </div>
                          </div>
                          <div className="div-row" style={{ justifyContent: 'flex-end' }}>
                            <div className="div-col-12">
                              {dataList.type === EnumMedicineCategory.中药 && (
                                <Form.Item {...formItemLayout12}>
                                  <span style={{ color: '#1FB8CD', marginLeft: '16px' }}>{this.getDecoctionUseTimesDesc(tableData.pre)}</span>
                                  {!!this.getProcessingMethod(tableData.pre) && (
                                    <span style={{ color: '#1FB8CD' }}>{this.getProcessingMethod(tableData.pre)}</span>
                                  )}
                                </Form.Item>
                              )}
                            </div>
                            <div className="div-col-12">
                              <Form.Item label={'合计'} labelCol={{ span: 18 }} wrapperCol={{ span: 6 }}>
                                {currencySymbol}
                                {tableData.total.toFixed(2)}
                              </Form.Item>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

          {/* 药品汇总 */}
          <MedicineSummary orderInfo={orderInfo} currencySymbol={currencySymbol} />

          {/* 分账信息*/}
          {/* {isSubAccount && <AccountInfo currencySymbol={currencySymbol} receivableAmount={receivableAmount} receivedAmount={receivedAmount} />} */}

          {/* 分润信息  */}
          <div className="div-row">
            <div className="div-col-3 text-align-right">{I18N.get('common_profit_info')}</div>
            <div className="div-col-15 order_info">
              <div className="div-row">
                <div className="div-col-24">
                  <Descriptions title="" bordered={false}>
                    <Descriptions.Item label={I18N.get('order_received_profit_amount')}>
                      {currencySymbol}
                      {orderInfo.user_profit_amount
                        ? MddDecimal.decimalCeil(Number(orderInfo.user_profit_amount.user_receivable_profit_amount || 0) / 100).toFixed(2)
                        : (0).toFixed(2)}
                    </Descriptions.Item>
                    {!!orderInfo.user_profit_amount && !!orderInfo.user_profit_amount.user_refund_profit_amount && (
                      <Descriptions.Item label="退款分润">
                        {currencySymbol}
                        {orderInfo.user_profit_amount
                          ? MddDecimal.decimalCeil(Number(orderInfo.user_profit_amount.user_refund_profit_amount || 0) / 100).toFixed(2)
                          : (0).toFixed(2)}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label={I18N.get('common_profit_status')}>
                      {orderInfo.user_profit_amount ? (orderInfo.user_profit_amount.distribute_success ? '已分润' : '未分润') : '未分润'}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </div>
            </div>
          </div>

          {/** 订单备注(医生备注) */}
          <div className="div-row">
            <div className="div-col-3 text-align-right">{I18N.get('common_order_remark')}</div>
            <div className="div-col-15 order_info">
              <div className="div-row">
                <div className="div-col-24">
                  <Form.Item style={{ padding: '10px 16px' }} wrapperCol={{ span: 24, offset: 0 }}>
                    {orderInfo.doctor_remarks || ''}
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>

          {isP && !!orderInfo.e_prescription_snapshot && (
            <div className="div-row">
              <div className="div-col-3 text-align-right" />
              <div className="div-col-15" style={{ paddingLeft: '16px', paddingBottom: '16px' }}>
                <Button onClick={() => this.handleShowDrawer(false)}>{I18N.get('order_electr_presc')}</Button>
              </div>
            </div>
          )}

          {/** 其它要求  */}
          {isShowOtherRequest && (
            <div className="div-row">
              <div className="div-col-3 text-align-right"> {I18N.get('common_order_other')} </div>
              <div className="div-col-15 order_info">
                <div className="div-row">
                  <div className="div-col-24">
                    <Form.Item style={{ marginBottom: '10px' }} wrapperCol={{ span: 24, offset: 0 }}>
                      {I18N.get('order_no_provide_prescription')}
                      <Popover content={I18N.get('order_no_provide_prescription_desc')} trigger="hover">
                        <Icon style={{ cursor: 'pointer' }} type="question-circle" />
                      </Popover>
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="div-row">
            <div className="div-col-3 text-align-right">{readonlyTextBox ? '备注' : I18N.get('common_remark_add')}</div>
            {readonlyTextBox ? (
              <div className="div-col-15 order_info">
                <Form.Item style={{ padding: '10px 16px' }} wrapperCol={{ span: 24, offset: 0 }}>
                  {orderInfo.agent_remarks || ''}
                </Form.Item>
              </div>
            ) : (
              <div className="div-col-15" style={{ paddingLeft: '16px' }}>
                <Form.Item wrapperCol={{ span: 24, offset: 0 }}>
                  {getFieldDecorator(`agent_remarks`, { initialValue: orderInfo.agent_remarks })(<TextArea rows={4} />)}
                </Form.Item>
              </div>
            )}
          </div>
          {/* 如果开通药品订单接口则显示药品清单发送按钮  */}
          {/* 若发送成功则刷新数据后可以发货 */}
          <SendMedicines orderInfo={orderInfo} sendMedicineConfig={sendMedicineConfig} callback={this.sendMedicineCallback} />

          <div className="div-row">
            <div className="div-col-3" />
            <div className="div-col-15 text-align-center">
              {!isAgent && (
                <React.Fragment>
                  {!readonlyTextBox && (
                    <Button type="primary" className="margin-right16" onClick={this.handleHold}>
                      {I18N.get('common_hold')}
                    </Button>
                  )}
                  <Button type="primary" className="margin-right16" onClick={this.printOrderList}>
                    {isP ? '打印处方' : I18N.get('common_print_order_medicine')}
                  </Button>
                  {orderInfo.pay_status === EPayStatus.待发货 && orderInfo.medicine_take_method !== 'SELF' && (
                    <Button type="primary" className="margin-right16" onClick={() => this.setState({ expressVisible: true })}>
                      {'打印运单'}
                    </Button>
                  )}
                  {sendMedicineConfig ? (
                    <span>
                      {orderInfo.pay_status === EPayStatus.待发货 && orderInfo.medicine_take_method !== 'SELF' && (
                        //  非药品订单需要发送药品清单
                        <Button
                          type="primary"
                          className="margin-right16"
                          disabled={orderInfo.category !== CATEGORY_ORIGINAL ? !sendMedicineOrderStatus : false}
                          onClick={() => this.handleSendGood()}
                        >
                          {'发货'}
                        </Button>
                      )}
                      {orderInfo.pay_status === EPayStatus.待发货 && orderInfo.medicine_take_method === 'SELF' && (
                        //  非药品订单需要发送药品清单
                        <Button
                          type="primary"
                          className="margin-right16"
                          disabled={orderInfo.category !== CATEGORY_ORIGINAL ? !sendMedicineOrderStatus : false}
                          onClick={() => this.handleSelfSendGood()}
                        >
                          {'发货'}
                        </Button>
                      )}
                    </span>
                  ) : (
                    <span>
                      {orderInfo.pay_status === EPayStatus.待发货 && orderInfo.medicine_take_method !== 'SELF' && (
                        <Button type="primary" className="margin-right16" onClick={() => this.handleSendGood()}>
                          {'发货'}
                        </Button>
                      )}
                      {orderInfo.pay_status === EPayStatus.待发货 && orderInfo.medicine_take_method === 'SELF' && (
                        <Button type="primary" className="margin-right16" onClick={() => this.handleSelfSendGood()}>
                          {'发货'}
                        </Button>
                      )}
                    </span>
                  )}
                  {orderInfo.has_deliver_list &&
                    <Button type="primary" className="margin-right16" onClick={() => this.getMedicineDeliverList(orderInfo.id)}>
                      发货清单
                    </Button>
                  }
                </React.Fragment>
              )}
              {isAgent && (
                <Button type="primary" onClick={this.printOrderList}>
                  {isP ? '打印处方' : I18N.get('common_print_order_medicine')}
                </Button>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 80 }} />
        </Form>

        <Drawer
          width={940}
          placement="right"
          closable={false}
          onClose={() => this.handleCloseDrawer()}
          visible={this.state.electrPrecsVisible}
          bodyStyle={{ height: '100%' }}
        >
          <iframe
            ref={(frame) => {
              this.iframe = frame;
            }}
            onLoad={() => this.onIframeLoaded()}
            src={drawUrl}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        </Drawer>

        {this.state.expressVisible && (
          <PrintExpressDialog
            onCancel={() => this.setState({ expressVisible: false })}
            onOk={() => this.setState({ expressVisible: false })}
            onExpressChange={(orderId: string, express: ILogistic) => this.handleNewExpress(orderId, express)}
            drugStoreInfo={drugStoreInfo}
            order={orderInfo}
          />
        )}
      </div>
    );
  }

  private async getStructureAddress() {
    let structureAddress;
    if (this.props.orderInfo.medicine_take_method === 'SELF') {
      structureAddress = this.props.orderInfo.self_mention_location;
    } else {
      structureAddress = this.props.orderInfo.receiver_structure_address;
    }
    const area = Number(structureAddress);
    const fullName = await this.fetchStructureAddress(area);
    this.setState({
      structureAddress: fullName,
    });
  }

  private async fetchStructureAddress(area: number): Promise<string> {
    if (area > 0) {
      const location = await OrderService.getLocationArea(area);
      if (location) {
        return location.full_name;
      }
    }

    return '';
  }

  private async getInvoiceInfo() {
    const res: IPageResult<any[]> = await OrderService.getInvoiceInfo(this.props.orderInfo.id);
    if (!!res && res.count > 0) {
      const invoice: IInvoiceInfo = res.results[0];
      this.setState({
        invoiceInfo: invoice,
      });
      this.setState({
        invoiceLogistics: {
          expressNumber: invoice.express_number || '',
          logisticsCompany: invoice.logistics_company || '',
        },
        invoiceIssuedStatus: invoice.issue_status === 'HAVE_BEEN_ISSUED',
      });

      if (invoice) {
        const area = Number(invoice.receiver_structure_address);
        const fullName = await this.fetchStructureAddress(area);
        this.setState({
          invoiceStructureAddress: fullName,
        });
      }
    }
  }

  private async getSendOrderMedicineConfig() {
    try {
      const config = await MallEdiService.getSendOrderMedicineConfig();
      if (!!config) {
        this.setState({
          sendMedicineConfig: config.is_enabled,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  private onIframeLoaded() {
    this.iframeLoaded = true;
    if (this.state.logisticSheet) {
      this.pushLogistic();
    } else {
      this.pushElectrPresc();
    }
  }

  private pushElectrPresc() {
    const { orderInfo } = this.props;
    if (!orderInfo.e_prescription_snapshot) {
      return;
    }

    if (this.iframe && this.iframe.contentWindow) {
      const domain = APIS.webUrl('');
      // const domain = 'http://localhost:4200/#/'
      // const msg: any = { type: 'EP', prescription: orderInfo.e_prescription_snapshot, fromSys: 'PARTNER' };
      const msg: any = { type: 'EP_PREVIEW', data: { order_id: orderInfo.id } };
      if (this.state.autoPrint) {
        msg.action = 'PRINT';
      }
      this.iframe.contentWindow.postMessage(JSON.stringify(msg), domain);
    }
  }

  private pushLogistic() {
    const { orderInfo } = this.props;

    if (this.iframe && this.iframe.contentWindow) {
      const domain = APIS.webUrl('');

      const msg: any = {
        type: 'LOGISTIC',
        logisticShowInfo: {
          order_no: orderInfo.out_trade_no,
          express_no: this.logisticData.express_no,
          express_company: this.logisticData.express_company,
          medical_record_code: orderInfo.medical_record_code,
          patient_name: orderInfo.patient_name,
          patient_mobile: orderInfo.patient_mobile,
          traces: this.logisticData.traces,
        },
      };

      this.iframe.contentWindow.postMessage(JSON.stringify(msg), domain);
    }
  }

  private handleNewExpress(orderId: string, express: ILogistic) {
    const { orderInfo } = this.props;
    orderInfo.express = express;
  }
}

const OrderDetailPage = Form.create<IOrderDetailFormProps>()(OrderDetailForm);
export default OrderDetailPage;



// WEBPACK FOOTER //
// ./src/components/views/Order/OrderDetailPage.tsx